import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useNavigationType, Navigate } from "react-router-dom";
import "./App.css";
import { LanguageProvider, CartProvider, SettingsProvider } from "./lib/providers";
import { CustomerAuthProvider } from "./lib/customerAuth";
import Header from "./components/Header";
import Hero from "./components/Hero";
import FeaturedBanner from "./components/FeaturedBanner";
import { useTrackPageView } from "./hooks/useTrackPageView";
import { ReviewsCarousel } from "./components/ReviewsPublic";
import ProductGrid from "./components/ProductGrid";
import Trust from "./components/Trust";
import TrustLogos from "./components/TrustLogos";
import Reseller from "./components/Reseller";
import About from "./components/About";
import Testimonials from "./components/Testimonials";
import Upsell from "./components/Upsell";
import Contact from "./components/Contact";
import DzShopperPage from "./pages/DzShopperPage";
import Footer from "./components/Footer";
import CartSheet from "./components/CartSheet";
import WhatsAppFloater from "./components/WhatsAppFloater";
import TrackingPage from "./components/TrackingPage";
import { PromoBar, FomoPopup } from "./components/Promo";
import { TrackingScripts } from "./components/TrackingScripts";
import ThankYouPage from "./pages/ThankYou";
import ProductPage from "./pages/ProductPage";
import CategoryPage from "./pages/CategoryPage";
import NotFoundPage from "./pages/NotFoundPage";
import CartPage from "./pages/CartPage";
import MonComptePage from "./pages/MonComptePage";
import VirementPage from "./pages/VirementPage";
import ReassuranceBlock from "./components/ReassuranceBlock";
import FAQ from "./components/FAQ";
import BetaBanner from "./components/BetaBanner";
import CollapsibleSection from "./components/CollapsibleSection";
import NewsletterSignup from "./components/NewsletterSignup";
import NewsletterExitIntent from "./components/NewsletterExitIntent";
import NewsletterConfirmPage from "./pages/NewsletterConfirmPage";
import NewsletterUnsubscribePage from "./pages/NewsletterUnsubscribePage";
import AuthConfirmPage from "./pages/AuthConfirmPage";
import AuthResetPasswordPage from "./pages/AuthResetPasswordPage";
import ReviewPage from "./components/ReviewPage";
import ReviewsPublicPage from "./components/ReviewsPublic";
import { useSettings } from "./lib/providers";
import { Toaster } from "sonner";

// Panneau d'admin chargé à la demande : les visiteurs ne téléchargent pas ce code.
const AdminPanel = lazy(() => import("./components/AdminPanel"));
const GuidesIndexPage = lazy(() => import("./pages/GuidesIndexPage"));
const GuidePage = lazy(() => import("./pages/GuidePage"));

// Default fallback order if settings haven't loaded yet or value is missing
const DEFAULT_SECTION_ORDER = [
  "upsell", "hero", "featured_banner", "reassurance", "products", "trust_logos", "about", "trust",
  "testimonials", "reviews_carousel", "newsletter", "reseller", "faq", "contact",
];

// Section ID → (Component, title-when-collapsed). Title is used only when section is collapsible.
const SECTION_REGISTRY = {
  hero:         { Component: Hero,             titleFr: "Accueil",            titleAr: "الرئيسية",       collapsible: false },
  featured_banner: { Component: FeaturedBanner, titleFr: "Coups de cœur",   titleAr: "المفضلات",       collapsible: true },
  reassurance:  { Component: ReassuranceBlock, titleFr: "Pourquoi nous choisir", titleAr: "لماذا تختارنا", collapsible: true },
  products:     { Component: ProductGrid,      titleFr: "Nos produits",       titleAr: "منتجاتنا",       collapsible: true },
  trust_logos:  { Component: TrustLogos,       titleFr: "Nos partenaires",    titleAr: "شركاؤنا",       collapsible: true },
  about:        { Component: About,            titleFr: "Qui suis-je",         titleAr: "من نحن",         collapsible: true },
  trust:        { Component: Trust,            titleFr: "Notre engagement",    titleAr: "التزامنا",       collapsible: true },
  testimonials: { Component: Testimonials,     titleFr: "Témoignages",         titleAr: "آراء العملاء",  collapsible: true },
  reviews_carousel: { Component: ReviewsCarousel, titleFr: "Avis clients vérifiés", titleAr: "آراء العملاء الموثقة", collapsible: true },
  newsletter:   { Component: () => <NewsletterSignup variant="section" source="homepage" />, titleFr: "Newsletter", titleAr: "النشرة الإخبارية", collapsible: false },
  reseller:     { Component: Reseller,         titleFr: "Revendeurs B2B",     titleAr: "الموزعون",      collapsible: true },
  upsell:       { Component: Upsell,           titleFr: "Suggestions",         titleAr: "اقتراحات",      collapsible: true },
  faq:          { Component: FAQ,              titleFr: "FAQ",                 titleAr: "الأسئلة الشائعة", collapsible: true },
  contact:      { Component: Contact,          titleFr: "Contact",             titleAr: "اتصل بنا",      collapsible: true },
};

function Shell() {
  const [adminOpen, setAdminOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSettings();

  // Custom secret admin path (configurable via .env REACT_APP_ADMIN_PATH)
  const ADMIN_PATH = "/" + (process.env.REACT_APP_ADMIN_PATH || "admin").replace(/^\/+/, "");

  // Allow direct access via the secret admin URL (bookmarkable)
  useEffect(() => {
    if (location.pathname === ADMIN_PATH) {
      setAdminOpen(true);
    }
  }, [location.pathname, ADMIN_PATH]);

  const closeAdmin = (open) => {
    setAdminOpen(open);
    if (!open && location.pathname === ADMIN_PATH) navigate("/", { replace: true });
  };

  // Resolve section order & collapsibility from settings (with safe fallback)
  const rawOrder = Array.isArray(settings?.home_section_order) && settings.home_section_order.length
    ? settings.home_section_order
    : DEFAULT_SECTION_ORDER;
  // Keep only known sections + dedupe + append any missing ones to the tail (so a new section
  // added in code stays visible until the admin reorders it explicitly).
  const seen = new Set();
  const order = [
    ...rawOrder.filter((id) => SECTION_REGISTRY[id] && !seen.has(id) && seen.add(id)),
    ...DEFAULT_SECTION_ORDER.filter((id) => !seen.has(id)),
  ];
  const collapsibleSet = new Set(Array.isArray(settings?.home_collapsible_sections) ? settings.home_collapsible_sections : []);

  return (
    <div className="min-h-screen text-white overflow-x-hidden">
      <BetaBanner />
      <PromoBar />
      <Header />
      <main>
        {order.map((id) => {
          const def = SECTION_REGISTRY[id];
          if (!def) return null;
          const { Component } = def;
          const isCollapsible = def.collapsible && collapsibleSet.has(id);
          if (isCollapsible) {
            return (
              <CollapsibleSection key={id} id={id} titleFr={def.titleFr} titleAr={def.titleAr}>
                <Component />
              </CollapsibleSection>
            );
          }
          return <Component key={id} />;
        })}
      </main>
      <Footer onAdminClick={() => setAdminOpen(true)} />
      <WhatsAppFloater />
      <CartSheet />
      {adminOpen && (
        <Suspense fallback={null}>
          <AdminPanel open={adminOpen} onOpenChange={closeAdmin} />
        </Suspense>
      )}
      {!adminOpen && <FomoPopup />}
      {!adminOpen && <NewsletterExitIntent />}
    </div>
  );
}

// Retour arrière (bouton précédent du téléphone / navigateur) : on remet la page à la hauteur où le client l'avait quittée.
// Le catalogue se charge en différé, donc on réessaie quelques instants.
function ScrollMemory() {
  const loc = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    const key = `scroll:${loc.key}`;
    const save = () => { try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* ignore */ } };
    window.addEventListener("scroll", save, { passive: true });
    let timer = null;
    if (navType === "POP") {
      const y = Number(sessionStorage.getItem(key) || 0);
      if (y > 0) {
        let tries = 0;
        timer = setInterval(() => {
          window.scrollTo(0, y);
          if (Math.abs(window.scrollY - y) < 5 || ++tries > 25) clearInterval(timer);
        }, 100);
      }
    }
    return () => { window.removeEventListener("scroll", save); if (timer) clearInterval(timer); };
  }, [loc.key, navType]);
  return null;
}

function VisitTracker() {
  useTrackPageView();
  return null;
}

function App() {
  const adminPath = (process.env.REACT_APP_ADMIN_PATH || "admin").replace(/^\/+/, "");  return (
    <LanguageProvider>
      <SettingsProvider>
        <CartProvider>
          <CustomerAuthProvider>
            <BrowserRouter>
              <TrackingScripts />
              <VisitTracker />
              <ScrollMemory />
              <Routes>
                <Route path="/" element={<Shell />} />
                <Route path={`/${adminPath}`} element={<Shell />} />
                <Route path="/produit/:id" element={<ProductPage />} />
                <Route path="/categorie/:slug" element={<CategoryPage />} />
                <Route path="/panier" element={<CartPage />} />
                <Route path="/merci/:orderId" element={<ThankYouPage />} />
                <Route path="/virement/:orderId" element={<VirementPage />} />
                <Route path="/mon-compte" element={<MonComptePage />} />
                <Route path="/dz-shopper" element={<DzShopperPage />} />
                <Route path="/track/:id" element={<TrackingPage />} />
                <Route path="/newsletter/confirm" element={<NewsletterConfirmPage />} />
                <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
                <Route path="/auth/confirm" element={<AuthConfirmPage />} />
                <Route path="/auth/reset-password" element={<AuthResetPasswordPage />} />
                <Route path="/review/:token" element={<ReviewPage />} />
                <Route path="/avis" element={<ReviewsPublicPage />} />
                <Route path="/guides" element={<Suspense fallback={null}><GuidesIndexPage /></Suspense>} />
                <Route path="/guides/:slug" element={<Suspense fallback={null}><GuidePage /></Suspense>} />
                {/* Catch-all: dedicated 404 page */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <Toaster theme="dark" position="top-center" />
            </BrowserRouter>
          </CustomerAuthProvider>
        </CartProvider>
      </SettingsProvider>
    </LanguageProvider>
  );
}

export default App;
